import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO from '../../components/SEO';
import { useCountry } from '../../context/CountryContext';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(true);
  const { country } = useCountry();

  const pricing = {
    ghana: {
      currency: 'GHS',
      main: { monthly: 300, yearly: 3000 },
      branch: { monthly: 200, yearly: 2000 },
      monthlyEquivalent: 250,
      paymentMethods: 'Mobile Money (MTN, Vodafone, AirtelTigo) and bank transfers'
    },
    nigeria: {
      currency: 'NGN',
      main: { monthly: 20000, yearly: 200000 },
      branch: { monthly: 14000, yearly: 140000 },
      monthlyEquivalent: 16667,
      paymentMethods: 'Bank transfers and card payments'
    }
  };

  const currentPricing = pricing[country] || pricing.ghana;
  const mainPrice = isYearly ? currentPricing.main.yearly : currentPricing.main.monthly;
  const branchPrice = isYearly ? currentPricing.branch.yearly : currentPricing.branch.monthly;
  const currency = currentPricing.currency;
  const period = isYearly ? 'year' : 'month';

  const allFeatures = [
    'Full Point of Sale', 'Inventory Management', 'Unlimited Staff Accounts',
    'Real-time Analytics & Reports', 'Expense Tracking', 'Multi-Branch Support',
    'Receipt Printing', 'Role-based Access Control', 'Works on Any Device', 'Offline Mode',
  ];

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="Affordable POS Software Pricing for Africa"
        description="Simple, transparent pricing for Smart POS Africa. One month free trial. POS software from GHS 300/month in Ghana and NGN 20,000/month in Nigeria. No hidden fees, no per-user charges."
        path="/pricing"
        keywords="pos software pricing Africa, affordable POS system Africa, POS cost Ghana, POS pricing Nigeria, cheap pos software Africa, business software pricing Ghana, pos software price"
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-44 pb-12 sm:pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <R>
            <div className="inline-flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/[0.12] rounded-full px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <span className="text-emerald-800 text-xs font-bold tracking-wide">Simple, transparent pricing</span>
            </div>
          </R>
          <R delay={60}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.25rem] font-extrabold text-primaryB-900 mb-4 leading-[1.08] tracking-tight">
              One plan, <span className="italic text-emerald-600">one price</span>.
            </h1>
          </R>
          <R delay={120}>
            <p className="text-base sm:text-lg text-primaryB-500 max-w-xl mx-auto mb-10 leading-relaxed">
              No tiers. No hidden fees. Every business gets access to everything.
            </p>
          </R>

          {/* Billing Toggle */}
          <R delay={200}>
            <div className="inline-flex items-center bg-primaryB-100 rounded-full p-1">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-5 sm:px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                  !isYearly ? 'bg-primaryB-50 text-primaryB-900 shadow-card' : 'text-primaryB-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-5 sm:px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                  isYearly ? 'bg-primaryB-50 text-primaryB-900 shadow-card' : 'text-primaryB-400'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs text-emerald-600 font-bold">-17%</span>
              </button>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ PRICING CARDS ━━━ */}
      <section className="pb-16 sm:pb-24 lg:pb-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Background shape */}
            <div className="absolute -top-8 -right-8 -bottom-8 -left-8 bg-emerald-800/[0.02] rounded-[2rem] hidden sm:block" />

            <div className="relative z-10 grid md:grid-cols-2 gap-6">
              {/* Main Plan */}
              <R delay={0}>
                <div className={`bg-primaryB-50 rounded-3xl p-6 sm:p-8 ${isYearly ? 'border-2 border-emerald-500 ring-1 ring-emerald-500/20' : 'border border-primaryB-200/60'} shadow-elevated hover:shadow-elevated transition-all duration-300 relative h-full`}>
                  {isYearly && (
                    <div className="absolute -top-3 left-6">
                      <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                        Most popular
                      </span>
                    </div>
                  )}

                  <div className="mb-6 pt-2">
                    <h2 className="text-xl font-bold text-primaryB-900 mb-1">Business</h2>
                    <p className="text-primaryB-300 text-sm mb-5">For single-location businesses</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl sm:text-5xl font-extrabold text-primaryB-900">{currency} {mainPrice.toLocaleString()}</span>
                      <span className="text-primaryB-300">/{period}</span>
                    </div>
                    {isYearly && (
                      <p className="text-emerald-600 text-sm mt-1.5 font-semibold">
                        {currency} {currentPricing.monthlyEquivalent.toLocaleString()}/month billed yearly
                      </p>
                    )}
                  </div>

                  <Link
                    to="/signup"
                    className="group flex items-center justify-center gap-2 w-full bg-primaryB-900 hover:bg-primaryB-800 text-white py-3.5 rounded-full font-bold transition-all duration-200 mb-6"
                  >
                    Start free trial
                    <ArrowRight weight="bold" className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>

                  <ul className="space-y-3">
                    {['1 Business Location', 'Unlimited Staff', 'All Features Included', 'Works Offline', 'Email & WhatsApp Support'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-primaryB-700 text-sm">
                        <span className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                          <Check weight="bold" className="w-3 h-3 text-emerald-500" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </R>

              {/* Additional Branch */}
              <R delay={150}>
                <div className="bg-primaryB-50 rounded-3xl p-6 sm:p-8 border border-primaryB-200/60 shadow-card hover:shadow-elevated transition-shadow duration-300 h-full">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-primaryB-900 mb-1">Additional Branch</h2>
                    <p className="text-primaryB-300 text-sm mb-5">Add more locations as you grow</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl sm:text-5xl font-extrabold text-primaryB-900">{currency} {branchPrice.toLocaleString()}</span>
                      <span className="text-primaryB-300">/{period}</span>
                    </div>
                    <p className="text-primaryB-300 text-sm mt-1.5">per additional branch</p>
                  </div>

                  <div className="bg-primaryB-50 rounded-xl p-4 mb-6 border border-primaryB-200/60 shadow-card">
                    <p className="text-primaryB-700 text-sm">
                      <strong className="text-primaryB-900">Example:</strong> 3 branches = {currency} {mainPrice.toLocaleString()} + {currency} {(branchPrice * 2).toLocaleString()} = <strong className="text-emerald-600">{currency} {(mainPrice + branchPrice * 2).toLocaleString()}/{period}</strong>
                    </p>
                  </div>

                  <ul className="space-y-3">
                    {['Same features as main branch', 'Separate inventory tracking', 'Branch-level reporting', 'Assign staff to branches'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-primaryB-700 text-sm">
                        <span className="w-5 h-5 rounded-full bg-primaryB-100 flex items-center justify-center flex-shrink-0">
                          <Check weight="bold" className="w-3 h-3 text-primaryB-400" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </R>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ STATS BAR ━━━ */}
      <section className="border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-primaryB-200/60">
            {[
              { value: '5 min', label: 'Setup time' },
              { value: '1 month', label: 'Free trial' },
              { value: '24/7', label: 'Access anywhere' },
            ].map((stat, index) => (
              <R key={stat.label} delay={index * 80}>
                <div className="py-6 sm:py-10 text-center px-2">
                  <div className="text-lg sm:text-3xl font-extrabold text-primaryB-900 mb-0.5 sm:mb-1">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-primaryB-300">{stat.label}</div>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ ALL FEATURES ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-3">
                Everything included
              </h2>
              <p className="text-primaryB-500 text-base sm:text-lg max-w-lg mx-auto">
                No feature gates. No upsells. You get the full product from day one.
              </p>
            </div>
          </R>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4 sm:gap-5">
            {allFeatures.map((feature, index) => (
              <R key={feature} delay={index * 60}>
                <div className="flex items-center gap-3 bg-primaryB-50 rounded-2xl px-4 py-3.5 border border-primaryB-200/60 shadow-card hover:shadow-card transition-shadow duration-200">
                  <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Check weight="bold" className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-primaryB-900 text-sm font-semibold">{feature}</span>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ TRUST / GUARANTEE ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 bg-primaryB-50 border-y border-primaryB-200/60">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-3">
                Start with confidence
              </h2>
              <p className="text-primaryB-500 text-base sm:text-lg max-w-lg mx-auto">
                No risk, no commitment. Just a better way to run your business.
              </p>
            </div>
          </R>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { title: 'No credit card required', description: 'Sign up and try everything free for a full month. No payment info needed to start.' },
              { title: 'Cancel anytime', description: 'No contracts, no lock-in. Cancel whenever you want and keep access until your billing period ends.' },
              { title: '5 minute setup', description: 'Create your account, add your products, and start selling. No technical knowledge needed.' },
            ].map((item, i) => (
              <R key={item.title} delay={i * 80}>
                <div className="bg-primaryB-50 rounded-3xl p-6 sm:p-7 border border-primaryB-200/60 text-center">
                  <p className="text-primaryB-900 font-bold text-base sm:text-lg mb-1.5">{item.title}</p>
                  <p className="text-primaryB-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default PricingPage;
